import React, { useState } from "react";
import { createRoot, Root } from "react-dom/client";
import {
  ToastProps,
  ToastType,
  ToastShowConfig,
  ToastHandler,
  ToastMethods,
} from "./Toast.types";
import { Button } from "../Button";
import "./Toast.scss";

// 导入图片
import SuccessImg from "@/assets/dialog/dialog_success.webp";
import FailedImg from "@/assets/dialog/dialog_failed.webp";
import ProcessingImg from "@/assets/dialog/dialog_processing.webp";

// 声明全局 i18next
declare global {
  interface Window {
    i18next?: {
      t: (key: string) => string;
    };
  }
}

/**
 * 获取翻译文本
 */
const getTranslation = (key: string, defaultValue: string): string => {
  if (typeof window !== "undefined" && window.i18next) {
    return window.i18next.t(key);
  }
  return defaultValue;
};

/**
 * 默认标题文案
 */
const getDefaultTitle = (type: ToastType): string => {
  switch (type) {
    case "success":
      return getTranslation("toast.success", "Success");
    case "failed":
      return getTranslation("toast.failed", "Failed");
    case "processing":
      return getTranslation("toast.processing", "Processing...");
    default:
      return "";
  }
};

/**
 * 图标映射
 */
const ToastIcons: Record<ToastType, string> = {
  success: SuccessImg,
  failed: FailedImg,
  processing: ProcessingImg,
};

/**
 * Toast组件
 */
const ToastComponent: React.FC<ToastProps & { visible: boolean }> = ({
  type,
  title,
  onClose,
  className = "",
  style = {},
  visible,
}) => {
  const [isLeaving, setIsLeaving] = useState(false);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose?.();
    }, 300); // 等待动画结束
  };

  const displayTitle = title || getDefaultTitle(type);
  const displayIcon = ToastIcons[type];

  if (!visible) return null;

  return (
    <div className="toast__mask">
      <div
        className={`toast toast--${type} ${isLeaving ? "toast--leaving" : ""} ${className}`}
        style={{ ...style }}
      >
        {/* 图标 */}
        <div className="toast__icon">
          <img src={displayIcon} alt={type} />
        </div>
        {/* 标题 */}
        <div className="toast__title">{displayTitle}</div>
      </div>

      {/* OK按钮 - 在弹窗外部 */}
      <div
        className={`toast__button-wrapper ${isLeaving ? "toast__button-wrapper--leaving" : ""}`}
      >
        <Button
          variant="primary"
          // size="sm"
          onClick={handleClose}
          className="toast__button"
          block
        >
          {getTranslation("common.ok", "OK")}
        </Button>
      </div>
    </div>
  );
};

/**
 * Toast容器管理
 */
class ToastManager {
  private container: HTMLDivElement | null = null;
  private root: Root | null = null;
  private currentToast: {
    component: React.ReactElement;
    timer?: NodeJS.Timeout;
  } | null = null;

  /**
   * 获取或创建容器
   */
  private getContainer(): HTMLDivElement {
    if (!this.container) {
      this.container = document.createElement("div");
      this.container.className = "toast-container";
      this.container.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 9999;
        pointer-events: none;
      `;
      document.body.appendChild(this.container);

      this.root = createRoot(this.container);
    }
    return this.container;
  }

  /**
   * 显示Toast
   */
  show(config: ToastShowConfig): ToastHandler {
    // 清除当前Toast
    this.clear();

    const container = this.getContainer();
    container.style.pointerEvents = "auto";

    const { type, title, onClose, className, style } = config;

    const handleClose = () => {
      if (this.root) {
        this.root.render(<></>);
      }
      if (container) {
        container.style.pointerEvents = "none";
      }
      this.currentToast = null;
      onClose?.();
    };

    const toastElement = (
      <ToastComponent
        type={type}
        title={title}
        onClose={handleClose}
        className={className}
        style={style}
        visible={true}
      />
    );

    this.currentToast = { component: toastElement };

    // 渲染Toast
    if (this.root) {
      this.root.render(toastElement);
    }

    return {
      close: handleClose,
    };
  }

  /**
   * 清除所有Toast
   */
  clear() {
    if (this.root) {
      this.root.render(<></>);
    }

    if (this.container) {
      this.container.style.pointerEvents = "none";
    }

    this.currentToast = null;
  }
}

// 创建全局实例
const toastManager = new ToastManager();

/**
 * Toast静态方法
 */
export const Toast: ToastMethods = {
  /**
   * 显示Toast
   */
  show: (config: ToastShowConfig | string): ToastHandler => {
    if (typeof config === "string") {
      return toastManager.show({
        type: "success",
        title: config,
      });
    }
    return toastManager.show(config);
  },

  /**
   * 成功Toast
   */
  success: (title): ToastHandler => {
    return toastManager.show({
      type: "success",
      title,
    });
  },

  /**
   * 失败Toast
   */
  failed: (title): ToastHandler => {
    return toastManager.show({
      type: "failed",
      title,
    });
  },

  /**
   * 进行中Toast
   */
  processing: (title): ToastHandler => {
    return toastManager.show({
      type: "processing",
      title,
    });
  },

  /**
   * 清除所有Toast
   */
  clear: (): void => {
    toastManager.clear();
  },
};

export type { ToastProps, ToastShowConfig, ToastHandler, ToastMethods };
